import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  Accordion, ActionIcon, Badge, Box, Loader, SegmentedControl, Text, TextInput, Tooltip,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  getMediaBankSnapshot,
  loadMediaBank,
  subscribeMediaBank,
} from '../../utils/mediaBankRegistry';
import {
  MEDIA_TYPES, mediaBankView, mediaThumbnailUrl, normalizeForSearch,
} from '../../../shared/media/mediaBank';
import {
  getWebLinksSnapshot,
  loadWebLinks,
  subscribeWebLinks,
} from '../../utils/webLinkRegistry';
import { isLinkUsableAs } from '../../media/mediaLinks';
import MediaPreviewModal from '../../media/MediaPreviewModal';

/**
 * Banque de médias de l'entreprise, dans la sidebar.
 *
 * Reprend la forme du picker de l'éditeur classique
 * (`components/Image/MediaBankPicker.jsx`) : dossiers repliables, vignettes sur
 * deux colonnes, le clic choisit le média. Il n'est pas réutilisable tel quel —
 * il est en Ant Design, le web-editor est en Mantine — mais sa logique l'est.
 *
 * Le chargement est déclenché au montage : ce composant n'est monté que quand
 * l'utilisateur ouvre l'onglet « Banque », jamais à l'ouverture de l'éditeur.
 *
 * Trois familles cohabitent ici (image, GIF, vidéo). Un GIF s'affiche tel quel,
 * une vidéo se prévisualise par son poster et se joue dans la modale : lire
 * chaque vidéo dans la grille chargerait des dizaines de mégaoctets pour
 * ouvrir un panneau.
 */

const THUMB_HEIGHT = 68;

function BankThumbnail({ media, onSelect, onPreview }) {
  const thumbnail = mediaThumbnailUrl(media);
  const isVideo = media.type === MEDIA_TYPES.video;

  return (
    /* minWidth 0 : sans lui la colonne de la grille s'élargit à la longueur du
       nom de fichier (min-width auto par défaut sur un item de grille) et le
       nom déborde de la sidebar au lieu d'être tronqué. */
    <Box style={{ minWidth: 0 }}>
      {/* Le cadre positionné s'arrête à la vignette : les pastilles se posent
          sur l'image, pas sur le nom qui la suit. */}
      <Box style={{ position: 'relative' }}>
        <Tooltip label={media.filename || 'Sans nom'} openDelay={400} withinPortal>
          <Box
            component="button"
            type="button"
            onClick={() => onSelect(media)}
            aria-label={`Choisir ${media.filename || 'ce média'}`}
            p={0}
            style={{
              display: 'block',
              width: '100%',
              height: THUMB_HEIGHT,
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 6,
              overflow: 'hidden',
              background: 'var(--mantine-color-gray-0)',
              cursor: 'pointer',
            }}
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt=""
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              /* Vidéo dont le poster n'a pas pu être extrait : un cadre nommé
                 vaut mieux qu'une vignette cassée. */
              <Text size="xs" c="dimmed" ta="center" mt={THUMB_HEIGHT / 2 - 10}>Vidéo</Text>
            )}
          </Box>
        </Tooltip>

        {isVideo && (
          <FontAwesomeIcon
            icon={icon({ name: 'play', style: 'solid' })}
            style={{
              fontSize: 16,
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.6))',
              pointerEvents: 'none',
            }}
            aria-hidden
          />
        )}

        {media.type === MEDIA_TYPES.gif && (
          <Badge
            size="xs"
            variant="filled"
            color="dark"
            style={{ position: 'absolute', bottom: 4, left: 4, pointerEvents: 'none' }}
          >
            GIF
          </Badge>
        )}

        <Tooltip label="Prévisualiser" withinPortal>
          <ActionIcon
            size="sm"
            variant="filled"
            color="dark"
            aria-label={`Prévisualiser ${media.filename || 'ce média'}`}
            onClick={() => onPreview(media)}
            style={{ position: 'absolute', top: 4, right: 4, opacity: 0.75 }}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'eye', style: 'light' })}
              style={{ fontSize: 13 }}
            />
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Deux photos de chantier se ressemblent : la vignette seule ne dit pas
          laquelle on pose. Le nom est tronqué sur une ligne (la colonne fait
          la moitié d'une sidebar), le nom entier reste dans l'infobulle. */}
      <Text size="10px" c="dimmed" truncate mt={2} title={media.filename}>
        {media.filename || 'Sans nom'}
      </Text>
    </Box>
  );
}

/** Onglet « Liens » : la banque de liens, à côté des familles de médias. */
const LINK_FAMILY = 'link';

function LinkRow({ link, onPick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onPick(link.url)}
      p="6px 4px"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
      }}
    >
      <Text size="xs" fw={500} truncate title={link.label}>{link.label}</Text>
      <Text size="10px" c="dimmed" truncate title={link.url}>{link.url}</Text>
    </Box>
  );
}

/** Recherche des liens : le libellé et l'adresse, la seconde étant souvent
    ce dont on se souvient (un nom de domaine). */
function filterLinkGroups(groups, query, kind) {
  const needle = normalizeForSearch(query).trim();
  return (groups || [])
    .map((group) => {
      const links = group.links.filter(
        (link) => isLinkUsableAs(link.url, kind)
          && (!needle
            || normalizeForSearch(link.label).includes(needle)
            || normalizeForSearch(link.url).includes(needle)),
      );
      return links.length ? { ...group, links } : null;
    })
    .filter(Boolean);
}

/**
 * @param onSelect reçoit le média normalisé — l'appelant décide du palier.
 * @param types familles admises ; `null` pour les trois.
 * @param onPickLink reçoit l'adresse d'un lien de la banque de liens ; absent,
 *   l'onglet « Liens » n'existe pas.
 * @param linkKind famille visée par les liens (`image` ou `video`).
 */
export default function MediaBankPicker({
  onSelect, types = null, onPickLink = null, linkKind = 'image',
}) {
  const { folders, status } = useSyncExternalStore(subscribeMediaBank, getMediaBankSnapshot);
  const linkBank = useSyncExternalStore(subscribeWebLinks, getWebLinksSnapshot);
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState('all');
  const [preview, setPreview] = useState(null);

  useEffect(() => { loadMediaBank(); }, []);

  const showsLinks = !!onPickLink && family === LINK_FAMILY;
  /* Paresse : la banque de liens n'est lue qu'à l'ouverture de son onglet, pas
     à celle de la banque de médias. */
  useEffect(() => { if (showsLinks) loadWebLinks(); }, [showsLinks]);

  const { scoped, visible, filters } = useMemo(
    () => mediaBankView(folders, { query, types, family }),
    [folders, query, types, family],
  );

  const linkGroups = useMemo(
    () => (showsLinks ? filterLinkGroups(linkBank.groups, query, linkKind) : []),
    [showsLinks, linkBank.groups, query, linkKind],
  );

  /* Les liens sont un onglet de plus, à côté des familles de médias. Quand la
     banque n'a qu'une famille, le segment « Tous » n'a rien à distinguer : il
     devient simplement « Médias », face à « Liens ». */
  const segments = onPickLink
    ? [...(filters.length ? filters : [{ value: 'all', label: 'Médias' }]),
      { value: LINK_FAMILY, label: 'Liens' }]
    : filters;

  const mediaPanel = () => {
    if (status === 'loading' || status === 'idle') {
      return (
        <Box ta="center" py="sm">
          <Loader size="xs" />
        </Box>
      );
    }

    /* « Pas de banque ici » et « la banque n'a pas pu être lue » sont deux
       choses différentes, et annoncer la seconde comme une banque vide envoie
       l'usager chercher des médias qu'un admin a pourtant déposés. */
    if (status === 'unavailable' || status === 'error') {
      return (
        <Text size="xs" c="dimmed" mt={6}>
          {status === 'error'
            ? 'La banque de médias n\'a pas pu être lue. Réessayez plus tard, ou envoyez un fichier.'
            : 'La banque de médias n\'est pas disponible pour ce document.'}
        </Text>
      );
    }

    if (scoped.length === 0) {
      return (
        <Text size="xs" c="dimmed" mt={6}>
          Aucun média disponible dans la banque de votre entreprise.
        </Text>
      );
    }

    if (visible.length === 0) return <Text size="xs" c="dimmed">Aucun résultat.</Text>;

    return (
      <Accordion
        multiple
        defaultValue={[String(visible[0].id)]}
        chevronPosition="left"
        styles={{
          control: { padding: '6px 4px' },
          content: { padding: '0 4px 8px' },
          item: { border: 'none' },
        }}
      >
        {visible.map((folder) => (
          <Accordion.Item key={folder.id} value={String(folder.id)}>
            <Accordion.Control>
              <Text size="xs" fw={600} component="span">
                {folder.name}
                <Text size="xs" c="dimmed" component="span" ml={4} fw={400}>
                  ({folder.media.length})
                </Text>
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {folder.media.map((media) => (
                  <BankThumbnail
                    key={media.id}
                    media={media}
                    onSelect={onSelect}
                    onPreview={setPreview}
                  />
                ))}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  const linkPanel = () => {
    if (linkBank.status === 'loading' || linkBank.status === 'idle') {
      return (
        <Box ta="center" py="sm">
          <Loader size="xs" />
        </Box>
      );
    }

    if (linkBank.status === 'error' || linkBank.status === 'unavailable') {
      return (
        <Text size="xs" c="dimmed" mt={6}>
          {linkBank.status === 'error'
            ? 'La banque de liens n\'a pas pu être lue. Réessayez plus tard.'
            : 'La banque de liens n\'est pas disponible pour ce document.'}
        </Text>
      );
    }

    if (linkGroups.length === 0) {
      return (
        <Text size="xs" c="dimmed" mt={6}>
          {linkBank.links.length === 0
            ? 'Aucun lien dans la banque de votre entreprise.'
            : 'Aucun résultat.'}
        </Text>
      );
    }

    return (
      <Accordion
        multiple
        defaultValue={[linkGroups[0].key]}
        chevronPosition="left"
        styles={{
          control: { padding: '6px 4px' },
          content: { padding: '0 4px 8px' },
          item: { border: 'none' },
        }}
      >
        {linkGroups.map((group) => (
          <Accordion.Item key={group.key} value={group.key}>
            <Accordion.Control>
              <Text size="xs" fw={600} component="span">
                {group.name}
                <Text size="xs" c="dimmed" component="span" ml={4} fw={400}>
                  ({group.links.length})
                </Text>
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              {group.links.map((link) => (
                <LinkRow key={link.id} link={link} onPick={onPickLink} />
              ))}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  return (
    <Box mt={6}>
      <TextInput
        size="xs"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        placeholder={showsLinks ? 'Rechercher un lien' : 'Rechercher un média'}
        leftSection={showsLinks
          ? <FontAwesomeIcon icon={icon({ name: 'link', style: 'light' })} style={{ fontSize: 14 }} />
          : <FontAwesomeIcon icon={icon({ name: 'magnifying-glass', style: 'solid' })} style={{ fontSize: 14 }} />}
        mb={6}
      />

      {segments.length > 1 && (
        <SegmentedControl
          size="xs"
          fullWidth
          value={family}
          onChange={setFamily}
          data={segments.map(({ value, label }) => ({ value, label }))}
          mb={6}
        />
      )}

      {showsLinks ? linkPanel() : mediaPanel()}

      <MediaPreviewModal
        opened={!!preview}
        onClose={() => setPreview(null)}
        kind={preview?.type === MEDIA_TYPES.video ? 'video' : 'image'}
        src={preview ? (preview.variants.web_lg?.url || preview.source) : ''}
        poster={preview?.posterUrl}
        title={preview?.filename}
      />
    </Box>
  );
}
